/**
 * Order Details Modal Component
 * 
 * This component displays detailed information about a specific order
 * including customer details, items, and approval actions.
 */

import React, { useState } from 'react';
import { useTranslations } from '../../context/TranslationsContext';
import { useApp } from '../../context/AppContext';

const OrderDetailsModal = ({ order, onClose, onApprove, onUpdatePrice }) => {
  const { translations } = useTranslations();
  const { state } = useApp();
  const t = translations[state.currentLanguage];

  const [adminNotes, setAdminNotes] = useState(order.admin_notes || '');
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [price, setPrice] = useState(order.total_price || '');

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US');
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'غير محدد';
    return new Intl.NumberFormat(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US').format(price);
  };

  const createGoogleMapsLink = (address) => {
    if (!address) return null;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  const handleApprove = (status) => {
    onApprove(order.id, status, adminNotes);
    setShowApprovalForm(false);
  };

  const handleUpdatePrice = () => {
    if (price && !isNaN(price) && price > 0) {
      onUpdatePrice(order.id, parseFloat(price));
      setShowPriceForm(false);
    }
  };

  if (!order) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{t.orderDetails}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-body">
          <div className="order-details-grid">
            <div className="detail-section">
              <h4>{t.orderInfo}</h4>
              <div className="detail-item">
                <span className="label">{t.orderNumber}:</span>
                <span className="value">#{order.id.substring(0, 8)}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.status}:</span>
                <span className="value">{t[order.status]}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.createdAt}:</span>
                <span className="value">{formatDate(order.created_at)}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.price}:</span>
                <div className="value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{formatPrice(order.total_price)} {order.total_price ? t.sar : ''}</span>
                  <button
                    onClick={() => setShowPriceForm(true)}
                    className="btn-secondary btn-sm"
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                  >
                    {order.total_price ? t.editPrice : t.setPrice}
                  </button>
                </div>
              </div>
              <div className="detail-item">
                <span className="label">{t.adminNotes}:</span>
                <span className="value">{order.admin_notes || t.noNotes}</span>
              </div>
            </div>

            <div className="detail-section">
              <h4>{t.customerInfo}</h4>
              <div className="detail-item">
                <span className="label">{t.customerName}:</span>
                <span className="value">{order.customer_name}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.phone}:</span>
                <span className="value">{order.customer_phone}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.address}:</span>
                <span className="value">
                  {order.customer_address ? (
                    <a 
                      href={createGoogleMapsLink(order.customer_address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="address-link"
                      style={{
                        color: '#1976d2',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                    >
                      {order.customer_address} 📍
                    </a>
                  ) : (
                    'غير محدد'
                  )}
                </span>
              </div>
            </div>

            <div className="detail-section">
              <h4>{t.storageDetails}</h4>
              <div className="detail-item">
                <span className="label">{t.space}:</span>
                <span className="value">{order.space_required} {t.sqm}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.duration}:</span>
                <span className="value">{order.duration_months} {t.months}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.items}:</span>
                <span className="value">{order.items_description}</span>
              </div>
              {order.additional_services && order.additional_services.length > 0 && (
                <div className="detail-item">
                  <span className="label">{t.additionalServices}:</span>
                  <span className="value">{order.additional_services.join(', ')}</span>
                </div>
              )}
            </div>

            {order.images && order.images.length > 0 && (
              <div className="detail-section">
                <h4>{t.images}</h4>
                <div className="images-grid">
                  {order.images.map((image, index) => (
                    <img
                      key={index}
                      src={`data:image/jpeg;base64,${image}`}
                      alt={`Order image ${index + 1}`}
                      className="order-image"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {order.status === 'pending' && (
            <div className="approval-section">
              <h4>{t.approvalActions}</h4>
              <div className="approval-actions">
                <button
                  onClick={() => setShowApprovalForm(true)}
                  className="btn-primary"
                >
                  {t.approve} / {t.reject}
                </button>
              </div>
              
              {showApprovalForm && (
                <div className="approval-form">
                  <div className="form-group">
                    <label>{t.adminNotes}:</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className="form-input"
                      rows="3"
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      onClick={() => handleApprove('approved')}
                      className="btn-success"
                    >
                      {t.approve}
                    </button>
                    <button
                      onClick={() => handleApprove('rejected')}
                      className="btn-danger"
                    >
                      {t.reject}
                    </button>
                    <button
                      onClick={() => setShowApprovalForm(false)}
                      className="btn-secondary"
                    >
                      {t.cancel}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Price Update Form */}
          {showPriceForm && (
            <div className="price-section">
              <h4>{t.setPrice}</h4>
              <div className="price-form">
                <div className="form-group">
                  <label>{t.priceSAR}:</label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="form-input"
                    placeholder={t.enterPrice}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-actions">
                  <button
                    onClick={handleUpdatePrice}
                    className="btn-primary"
                    disabled={!price || isNaN(price) || price <= 0}
                  >
                    {t.savePrice}
                  </button>
                  <button
                    onClick={() => setShowPriceForm(false)}
                    className="btn-secondary"
                  >
                    {t.cancel}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
